import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-712">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-713">
        <div className="max-w-4xl mx-auto text-center" data-id="element-714">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-715">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-716">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-717">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-718">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-719">
            <p data-id="element-720">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-721">
              <li data-id="element-722">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-723">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-724">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-725">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-726">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-727">
              <ul className="list-disc pl-6 space-y-2" data-id="element-728">
                <li data-id="element-729">
                  <strong data-id="element-730">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-731">
                  <strong data-id="element-732">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-733">
                  <strong data-id="element-734">Do we receive information from third parties?</strong>{' '}
                  We do not receive information from third parties.
                </li>
                <li data-id="element-735">
                  <strong data-id="element-736">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-737">
                  <strong data-id="element-738">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-739">
                  <strong data-id="element-740">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-741">
                  <strong data-id="element-742">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-743">
                  <strong data-id="element-744">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 11).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-745">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-746">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-747">
              <li data-id="element-748">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-749">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-750">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-751">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-752">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-753">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-754">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-755">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-756">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-757">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-758">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-759">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-760">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-761">
              <h3 className="font-bold text-lg" data-id="element-762">
                Personal information you disclose to us
              </h3>
              <p data-id="element-763">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-764">
                <strong data-id="element-765">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-766">
                <li data-id="element-767">Names</li>
                <li data-id="element-768">Phone numbers</li>
                <li data-id="element-769">Email addresses</li>
                <li data-id="element-770">Mailing addresses</li>
                <li data-id="element-771">Education level and interests</li>
                <li data-id="element-772">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-773">
                Information automatically collected
              </h3>
              <p data-id="element-774">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-775">
                <li data-id="element-776">IP address</li>
                <li data-id="element-777">Browser and device characteristics</li>
                <li data-id="element-778">Operating system</li>
                <li data-id="element-779">Language preferences</li>
                <li data-id="element-780">Referring URLs</li>
                <li data-id="element-781">Device name and type</li>
                <li data-id="element-782">Pages viewed and time spent</li>
                <li data-id="element-783">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-784">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-785">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-786">
              <p data-id="element-787">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-788">
                <li data-id="element-789">To facilitate account creation and authentication</li>
                <li data-id="element-790">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-791">To respond to user inquiries and offer support</li>
                <li data-id="element-792">To send administrative information to you</li>
                <li data-id="element-793">To fulfill and manage your requests</li>
                <li data-id="element-794">To enable user-to-user communications</li>
                <li data-id="element-795">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-796">To send you marketing and promotional communications</li>
                <li data-id="element-797">To deliver targeted advertising to you</li>
                <li data-id="element-798">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-799">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-800">To save or protect an individual's vital interest</li>
                <li data-id="element-801">To comply with our legal obligations</li>
              </ul>
            </div>
          </div>

          {/* 3. When and With Whom Do We Share */}
          <div data-id="element-802">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-803">
              3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-804">
              <p data-id="element-805">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-806">
                <li data-id="element-807">
                  <strong data-id="element-808">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-809">
                  <strong data-id="element-810">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-811">
                  <strong data-id="element-812">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-813">
                  <strong data-id="element-814">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-815">
                  <strong data-id="element-816">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Cookies and Tracking */}
          <div data-id="element-817">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-818">
              4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-819">
              <p data-id="element-820">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 5. How Long Do We Keep */}
          <div data-id="element-821">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-822">
              5. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-823">
              <p data-id="element-824">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 6. How Do We Keep Information Safe */}
          <div data-id="element-825">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-826">
              6. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-827">
              <p data-id="element-828">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 7. Minors */}
          <div data-id="element-829">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-830">
              7. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-831">
              <p data-id="element-832">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 8. Privacy Rights */}
          <div data-id="element-833">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-834">
              8. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-835">
              <p data-id="element-836">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-837">
                <li data-id="element-838">The right to access your personal information</li>
                <li data-id="element-839">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-840">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-841">The right to opt out of marketing communications</li>
                <li data-id="element-842">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-843">
                To exercise these rights, please contact us using the
                information in Section 11.
              </p>
            </div>
          </div>

          {/* 9. Do Not Track */}
          <div data-id="element-844">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-845">
              9. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-846">
              <p data-id="element-847">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 10. Updates */}
          <div data-id="element-848">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-849">
              10. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-850">
              <p data-id="element-851">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 11. Contact Us */}
          <div data-id="element-852">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-853">
              11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-854">
              <p data-id="element-855">
                If you have questions or comments about this notice, please
                contact us through the information provided on our website.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}